import { Badge, Collapse, Space, Typography, theme } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Problem, Selection } from "./types";

const { Text } = Typography;

type Props = {
  problems: Problem[];
  onSelect: (selection: Selection) => void;
  /** Opens the lifecycle drawer for problems that belong to a status. */
  onOpenLifecycle: () => void;
};

/**
 * Everything wrong with the draft, in one list, each item a link to the thing
 * that is wrong.
 *
 * The server also validates, but only when you press Save — which turned a typo
 * into a round trip and a list of strings to reconcile against the outline by
 * eye. Clicking an entry here selects the offending node.
 */
const ProblemList = ({ problems, onSelect, onOpenLifecycle }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const errors = problems.filter((p) => p.severity === "error");
  const warnings = problems.filter((p) => p.severity === "warning");

  if (problems.length === 0) {
    return (
      <Space size={6}>
        <CheckCircleFilled style={{ color: token.colorSuccess }} />
        <Text type="secondary">{t("Nothing wrong with this process.")}</Text>
      </Space>
    );
  }

  const row = (problem: Problem, i: number) => (
    <div
      key={i}
      role="button"
      tabIndex={0}
      onClick={() =>
        problem.target ? onSelect(problem.target) : onOpenLifecycle()
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (problem.target) onSelect(problem.target);
          else onOpenLifecycle();
        }
      }}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "4px 6px",
        borderRadius: token.borderRadius,
        cursor: "pointer",
      }}
    >
      {problem.severity === "error" ? (
        <ExclamationCircleFilled
          style={{ color: token.colorError, marginTop: 3 }}
        />
      ) : (
        <WarningFilled style={{ color: token.colorWarning, marginTop: 3 }} />
      )}
      <Text style={{ flex: 1, fontSize: 13 }}>
        {t(problem.message, problem.params)}
      </Text>
    </div>
  );

  return (
    <Collapse
      size="small"
      ghost
      defaultActiveKey={errors.length > 0 ? ["problems"] : []}
      items={[
        {
          key: "problems",
          label: (
            <Space size={10}>
              {errors.length > 0 && (
                <Badge
                  count={errors.length}
                  style={{ backgroundColor: token.colorError }}
                />
              )}
              {warnings.length > 0 && (
                <Badge
                  count={warnings.length}
                  style={{ backgroundColor: token.colorWarning }}
                />
              )}
              <Text>
                {errors.length > 0
                  ? t("This process cannot be published yet")
                  : t("Worth a look before publishing")}
              </Text>
            </Space>
          ),
          children: (
            <div>
              {errors.map(row)}
              {warnings.map((p, i) => row(p, errors.length + i))}
            </div>
          ),
        },
      ]}
    />
  );
};

export default ProblemList;
